/**
 * What a frontend module may contribute: pages (each with its guard and its
 * menu entry), components for named slots on core's screens, and card types
 * for the dashboard.
 *
 * Type-only imports from core, so core may import this file — like the
 * backend's `modules/types.ts`, it must never pull a module in at run time.
 */
import type { ComponentType, ReactElement } from 'react';
import type { SvgIconComponent } from '@mui/icons-material';
import type { DashboardCardWidth, UserDto } from '@forsys/shared';
import type { en } from '../i18n/resources';
import type { CoreSectionId } from '../layout/navigation';
import type { MODULE_LOCALES } from './locales';

type ModuleCatalogues = typeof MODULE_LOCALES.en;

/** The keys of `T` whose value is a string — a label, not a nested object. */
type StringKeys<T> = Extract<{ [K in keyof T]: T[K] extends string ? K : never }[keyof T], string>;

/** The strings of `T`, and those one level down: `title`, `stat.users`. */
type Leaves<T> = {
  [K in keyof T & string]: T[K] extends string ? K : `${K}.${StringKeys<T[K]>}`;
}[keyof T & string];

/**
 * `<module>:nav.<item>` — a string in a module namespace's `nav` object.
 *
 * Derived from the module's English catalogue, so a menu entry with no
 * translation is a compile error, exactly as it is for core's. A module whose
 * catalogue has no `nav` object contributes no keys, and so cannot declare one.
 * `nav.section` is an object, so it is not a label.
 */
export type ModuleNavKey = {
  [NS in keyof ModuleCatalogues]: ModuleCatalogues[NS] extends { readonly nav: infer Nav }
    ? `${NS}:nav.${StringKeys<Nav>}`
    : never;
}[keyof ModuleCatalogues];

/** The section headings in each module catalogue's `nav.section`, by namespace. */
type SectionsOf<NS extends keyof ModuleCatalogues> = ModuleCatalogues[NS] extends {
  readonly nav: { readonly section: infer Sections };
}
  ? StringKeys<Sections>
  : never;

/** `<module>:nav.section.<id>` — the heading of a section a module declares. */
export type ModuleSectionKey = {
  [NS in keyof ModuleCatalogues]: `${NS}:nav.section.${SectionsOf<NS>}`;
}[keyof ModuleCatalogues];

/**
 * Every section id some module's catalogue has a heading for. Several modules
 * may have the same one — that is how two modules share a section.
 */
export type ModuleSectionId = { [NS in keyof ModuleCatalogues]: SectionsOf<NS> }[keyof ModuleCatalogues];

/** Where a page's menu entry may go: one of core's sections, or one a module declares. */
export type NavSectionId = CoreSectionId | ModuleSectionId;

/**
 * A sidebar section a module needs and core does not have — "Sales",
 * "Settings". It belongs to no module: every module that puts a page in it
 * declares it, and the first in `MODULES` order gives the heading and the
 * position. See `navigationWith` in `layout/navigation.ts`.
 */
export interface ModuleSection {
  readonly id: ModuleSectionId;
  /** In this module's own namespace, so the module stands alone. */
  readonly titleKey: ModuleSectionKey;
  /**
   * Position against core's sections, which are 100 (Overview), 300 (Content),
   * 400 (People), 500 (Notifications) and 600 (History).
   */
  readonly order: number;
}

/** A page's entry in the sidebar. */
export interface ModuleNav {
  /** A core section, or one this module declares in `sections`. */
  readonly section: NavSectionId;
  readonly labelKey: ModuleNavKey;
  readonly icon: SvgIconComponent;
  /** Highlight on the exact path only. See `NavItem.exact`. */
  readonly exact?: boolean;
}

export interface ModulePage {
  /** Relative to the root, like core's routes: `messages`, `messages/:id`. */
  readonly path: string;
  readonly element: ReactElement;
  /**
   * Holding any of these reaches the page. **The menu entry inherits it**, so
   * the route guard and the nav item cannot disagree — the rule core's
   * `App.tsx` and `navigation.ts` have to keep by hand.
   */
  readonly anyOf?: readonly string[];
  /** Omit for a page with no menu entry of its own, like a detail screen. */
  readonly nav?: ModuleNav;
}

/**
 * Every slot core renders, and what it hands its contributions.
 *
 * A slot is added here **when a module needs one**, in the same change that
 * places `<Slot>` on the screen. An empty slot "for later" is a speculative
 * hook — the thing this design avoids.
 */
export interface SlotProps {
  /** The header, left of the notification bell. Something that wants noticing. */
  'header.actions': Record<string, never>;
  /** A user's detail screen, beside the account's own actions. */
  'user.detail.actions': { readonly user: UserDto };
  /**
   * A user's detail screen, after the account and its memberships: what a
   * module knows about this person, each in its own `Panel`.
   */
  'user.detail.sections': { readonly user: UserDto };
  /**
   * The caller's own profile, below their details and password: a module's
   * preferences, each in its own `Panel`. The caller is `useAuth().user`.
   */
  'profile.sections': Record<string, never>;
}

export type SlotName = keyof SlotProps;

export interface SlotContribution<N extends SlotName> {
  /** Unique within the module; with the module's key, the React key. */
  readonly id: string;
  /** Rendered only for a caller holding any of these. Presentation, not protection. */
  readonly anyOf?: readonly string[];
  readonly component: ComponentType<SlotProps[N]>;
}

export type ModuleSlots = { readonly [N in SlotName]?: readonly SlotContribution<N>[] };

// --- dashboard cards ---------------------------------------------------------

/** `<module>:dashboard.<key>` — a string in a module catalogue's `dashboard` object. */
export type ModuleDashboardKey = {
  [NS in keyof ModuleCatalogues]: ModuleCatalogues[NS] extends { readonly dashboard: infer D }
    ? `${NS}:dashboard.${Leaves<D>}`
    : never;
}[keyof ModuleCatalogues];

/** `system:dashboard.<key>` — core's own dashboard strings. */
export type CoreDashboardKey = `system:dashboard.${Leaves<typeof en.system.dashboard>}`;

/** Any text a dashboard card type names: its title, a field's label, an option's label. */
export type DashboardTextKey = CoreDashboardKey | ModuleDashboardKey;

/**
 * A card's settings. Values are plain, so that a stored config can be checked
 * against `defaultConfig` without knowing the card: see `normaliseConfig`.
 */
export type DashboardConfig = Readonly<Record<string, string | number | boolean>>;

/** What `defaults` and a field's `options` may ask about the caller. */
export interface DashboardContext {
  readonly can: (permission: string) => boolean;
  readonly canAny: (...permissions: string[]) => boolean;
}

/** A choice in a `select` field: translated text, or text from data (a machine's name). */
export type DashboardOption =
  | { readonly value: string; readonly labelKey: DashboardTextKey }
  | { readonly value: string; readonly label: string };

/**
 * One setting, declared; core renders the form. **Only `select` so far** — a
 * kind is added when a card first needs it. See `TODO.md`.
 */
export interface DashboardSelectField {
  readonly kind: 'select';
  /** The key in the config. */
  readonly key: string;
  readonly labelKey: DashboardTextKey;
  /** Loaded when the settings open. Only what the caller may choose. */
  readonly options: (
    ctx: DashboardContext,
  ) => readonly DashboardOption[] | Promise<readonly DashboardOption[]>;
}

export type DashboardSettingsField = DashboardSelectField;

/** A card that appears without the user adding it. `key` is stable: `users`, `all`. */
export interface DashboardDefault<C extends DashboardConfig> {
  readonly key: string;
  readonly config: C;
}

/**
 * A kind of card on the dashboard. Each card on a person's dashboard is one of
 * these with its own config, width and place.
 *
 * **A config shape is a contract with stored data.** Rows outlive the code
 * that wrote them: add a field only with a default in `defaultConfig`, and
 * never rename a field or change what its values mean.
 */
export interface DashboardCardType<C extends DashboardConfig = DashboardConfig> {
  /** Unique within the module. With the module's key, the stored type: `coffee:machine`. */
  readonly id: string;
  readonly titleKey: DashboardTextKey;
  readonly icon: SvgIconComponent;
  /** Shown only to a caller holding any of these. Presentation, not protection. */
  readonly anyOf?: readonly string[];
  /**
   * Gets a config already checked against `defaultConfig`. It still reads it
   * as a choice that may have gone — a machine deleted, a permission lost —
   * and then renders nothing, so the cell closes.
   */
  readonly component: ComponentType<{ readonly config: C }>;
  /** The preferred width, in columns. */
  readonly width: DashboardCardWidth;
  /** Default 1. */
  readonly minWidth?: DashboardCardWidth;
  /** Default 3. */
  readonly maxWidth?: DashboardCardWidth;
  /** Weight: a bigger number is lower on the dashboard. Core's stats are 100, its activity 900. */
  readonly order: number;
  /** Also every key a stored config may have, and the value for a missing or wrong one. */
  readonly defaultConfig: C;
  readonly settings?: readonly DashboardSettingsField[];
  /** What appears without the user adding it. None when omitted: the user adds it from the list. */
  readonly defaults?: (
    ctx: DashboardContext,
  ) => readonly DashboardDefault<C>[] | Promise<readonly DashboardDefault<C>[]>;
}

/**
 * Checks a card type where it is written, and erases its config type so that
 * card types with different configs fit in one list. Returns it unchanged.
 */
export function defineDashboardCard<C extends DashboardConfig>(card: DashboardCardType<C>): DashboardCardType {
  return card as unknown as DashboardCardType;
}

export interface FrontendModule {
  /** The backend module's key. Also its translation namespace. */
  readonly key: string;
  /** Every non-core section this module's pages use. */
  readonly sections?: readonly ModuleSection[];
  readonly pages?: readonly ModulePage[];
  readonly slots?: ModuleSlots;
  /** Card types for the dashboard. Each through `defineDashboardCard`. */
  readonly dashboardCards?: readonly DashboardCardType[];
}

/** Checks a manifest against `FrontendModule` where it is written. Returns it unchanged. */
export function defineFrontendModule<const M extends FrontendModule>(module: M): M {
  return module;
}
